import React, {createContext, PropsWithChildren, useContext, useEffect, useMemo, useState} from "react";
import {EventEmitter} from "nate-react-api-helpers";
import {Column} from "../../../../../misc/scroller/Table";
import {Workspace} from "../../elevationTool/model/view/MdViewSettings";
import {isDrwDefault} from "../drwDefaultElevation";
import {SidebarTab} from "./useSidebarElevationApp";

export type ElevationCellKind = Workspace;

// the column names of the three elevation cells in both openings tables. They are also the
// table-pref keys of those columns, so they can't change.
export const elevationColumnNames: Record<ElevationCellKind, string> = {
    screen: "Screen Elevation",
    door: "Door Elevation",
    hardware: "HW Elevation",
};

// the Anchor column: its cell opens the screen elevation on the Anchors tab when the drawing owns
// the anchors (the "(screen)" cell, see AnchorCol).
export const anchorColumnName = "Anchor";

export function elevationCellKind(columnName: string): ElevationCellKind | null {
    for(const kind of Object.keys(elevationColumnNames) as ElevationCellKind[]) {
        if(elevationColumnNames[kind] === columnName) return kind;
    }
    return null;
}

// what a focused elevation cell previews: an elevation kind and the sidebar tab to land on, or
// null for a column that isn't one.
export function elevationCellTarget(row: ElevationRow, columnName: string): {kind: ElevationCellKind; tab: SidebarTab} | null {
    if(columnName === anchorColumnName) {
        return row.drwHasAnchors ? {kind: "screen", tab: "anchors"} : null;
    }

    const kind = elevationCellKind(columnName);
    return kind ? {kind, tab: kind} : null;
}

// the other cells whose value has a file on the row (a pdf or image): the sidebar shows the file.
const fileCellColumns: {column: string; name: (row: ElevationRow) => string; files: (row: ElevationRow) => ElevationFile[]}[] = [
    {
        column: "Wall Type",
        name: row => row.drawingWallLabel || "",
        files: row => row.drawingWallLabelFile ? [{label: "Wall Type", file: row.drawingWallLabelFile, fileName: row.drawingWallLabelFileName}] : [],
    },
    {
        column: "Frame Profile",
        name: row => row.frameProfile || "",
        files: row => row.frameProfileFile ? [{label: "Frame Profile", file: row.frameProfileFile, fileName: row.frameProfileFileName ?? undefined}] : [],
    },
];

// one of the files of a legacy (file-based) elevation. A pair's door elevation may have one
// per leaf.
export type ElevationFile = {
    label: string;
    file: number;
    fileName?: string;
};

// what an opening has for an elevation kind: a drw elevation the tool draws (linked or
// default), a legacy file-based one, or nothing.
export type ElevationSource =
    | {type: "drw"}
    | {type: "file"; name: string; files: ElevationFile[]}
    | null;

// the cell the user has focused in the openings table: an elevation cell, or a cell whose value
// has a file.
export type ElevationCellFocus = {
    openingId: number;
    openingName: string;
    // the opening fields the preview draws from; a change reloads it.
    fingerprint: string;
} & ({
    type: "elevation";
    kind: ElevationCellKind;
    // the sidebar tab the cell lands on: the kind, or anchors for the Anchor column.
    tab: SidebarTab;
    sources: Record<ElevationCellKind, ElevationSource>;
} | {
    type: "file";
    // the column name.
    cell: string;
    // the cell's value.
    name: string;
    files: ElevationFile[];
});

// true when the focused cell has something the sidebar can show.
export function cellFocusShowable(focus: ElevationCellFocus): boolean {
    return focus.type === "elevation" ? !!focus.sources[focus.kind] : focus.files.length > 0;
}

// the focus payload for a cell, or null for a column the sidebar doesn't preview.
export function cellFocusFor(row: ElevationRow, columnName: string): ElevationCellFocus | null {
    const base = {openingId: row.id, openingName: row.name, fingerprint: elevationFingerprint(row)};

    const target = elevationCellTarget(row, columnName);
    if(target) {
        return {
            ...base,
            type: "elevation",
            kind: target.kind,
            tab: target.tab,
            sources: {
                screen: elevationSource(row, "screen"),
                door: elevationSource(row, "door"),
                hardware: elevationSource(row, "hardware"),
            },
        };
    }

    const fileCell = fileCellColumns.find(c => c.column === columnName);
    if(fileCell) {
        return {...base, type: "file", cell: columnName, name: fileCell.name(row), files: fileCell.files(row)};
    }

    return null;
}

// the fields of an Opening / QuoteOpening row the preview reads.
export type ElevationRow = {
    id: number;
    name: string;
    drwScreenElevation?: number;
    drwDoorElevation?: number;
    drwHardwareElevation?: number;
    drwScreenRendering?: number;
    drwDoorRendering?: number;
    drwHardwareRendering?: number;
    drwScreenPrimaryOpening?: number | null;
    drwScreenSlot?: string | null;
    drwHasAnchors?: boolean;
    drwAnchorSummary?: string | null;
    screenElevation?: string;
    screenElevationFile?: number;
    screenElevationFileName?: string;
    doorElevation?: string;
    doorElevationFile?: number;
    doorElevationFileName?: string;
    inactiveDoorElevation?: string;
    inactiveDoorElevationFile?: number;
    inactiveDoorElevationFileName?: string;
    nominalWidth?: string;
    inactiveDoorWidth?: string;
    nominalHeight?: string;
    openingType?: string;
    handing?: string;
    frameProfile?: string;
    frameProfileFile?: number | null;
    frameProfileFileName?: string | null;
    drawingWallLabel?: string;
    drawingWallLabelFile?: number;
    drawingWallLabelFileName?: string;
    hardwareGroup?: number;
    updatedAt?: string;
};

export function elevationSource(row: ElevationRow, kind: ElevationCellKind): ElevationSource {
    switch(kind) {
        case "screen":
            if(row.drwScreenElevation || row.drwScreenPrimaryOpening || isDrwDefault(row, "screen")) return {type: "drw"};
            if(row.screenElevation || row.screenElevationFile) {
                return {
                    type: "file",
                    name: row.screenElevation || "",
                    files: row.screenElevationFile ? [{label: "Screen", file: row.screenElevationFile, fileName: row.screenElevationFileName}] : [],
                };
            }
            return null;
        case "door": {
            if(row.drwDoorElevation || isDrwDefault(row, "door")) return {type: "drw"};
            const active = row.doorElevation || "";
            const inactive = row.inactiveDoorElevation || "";
            if(!active && !inactive && !row.doorElevationFile && !row.inactiveDoorElevationFile) return null;

            const files: ElevationFile[] = [];
            if(row.doorElevationFile) files.push({label: "Active", file: row.doorElevationFile, fileName: row.doorElevationFileName});
            if(row.inactiveDoorElevationFile && row.inactiveDoorElevationFile !== row.doorElevationFile) {
                files.push({label: "Inactive", file: row.inactiveDoorElevationFile, fileName: row.inactiveDoorElevationFileName});
            }
            return {type: "file", name: inactive && inactive !== active ? `${active}, ${inactive}` : active, files};
        }
        case "hardware":
            if(row.drwHardwareElevation || isDrwDefault(row, "hardware")) return {type: "drw"};
            return null;
    }
}

export function elevationFingerprint(row: ElevationRow): string {
    return [
        row.drwScreenElevation, row.drwDoorElevation, row.drwHardwareElevation,
        row.drwScreenRendering, row.drwDoorRendering, row.drwHardwareRendering,
        row.drwScreenPrimaryOpening, row.drwScreenSlot, row.drwHasAnchors, row.drwAnchorSummary,
        row.screenElevation, row.screenElevationFile,
        row.doorElevation, row.doorElevationFile, row.inactiveDoorElevation, row.inactiveDoorElevationFile,
        row.nominalWidth, row.inactiveDoorWidth, row.nominalHeight,
        row.openingType, row.handing, row.frameProfile, row.frameProfileFile, row.hardwareGroup, row.updatedAt,
        row.drawingWallLabel, row.drawingWallLabelFile,
    ].map(v => v ?? "").join("|");
}

// the focused elevation cell, or null while none is. An emitter (not state) so a focus change
// re-renders the sidebar only, not the table under the provider.
const ElevationSidebarContext = createContext<EventEmitter<ElevationCellFocus | null> | null>(null);

// whether the sidebar panel is showing, for the page to make room (see UnlessElevationSidebarOpen).
const ElevationSidebarOpenContext = createContext<{open: boolean; setOpen: (open: boolean) => void}>({
    open: false,
    setOpen: () => {},
});

export function ElevationSidebarProvider(props: PropsWithChildren<{}>) {
    const focus = useMemo(() => new EventEmitter<ElevationCellFocus | null>("elevationCellFocus"), []);
    const [open, setOpen] = useState(false);
    const openCtx = useMemo(() => ({open, setOpen}), [open]);

    return (
        <ElevationSidebarContext.Provider value={focus}>
            <ElevationSidebarOpenContext.Provider value={openCtx}>
                {props.children}
            </ElevationSidebarOpenContext.Provider>
        </ElevationSidebarContext.Provider>
    );
}

export function useElevationCellFocusEmitter() {
    return useContext(ElevationSidebarContext);
}

export function useElevationSidebarOpen() {
    return useContext(ElevationSidebarOpenContext);
}

// renders its children only while the sidebar panel is closed.
export function UnlessElevationSidebarOpen(props: PropsWithChildren<{}>) {
    const {open} = useElevationSidebarOpen();
    if(open) return null;
    return <>{props.children}</>;
}

// reports an elevation cell while it is focused. Rendered by the table for the focused cell
// (see cellCustomize.onFocus); other cells report nothing.
export function ElevationCellFocusReporter(props: {row: ElevationRow; column: Column<any>}) {
    const emitter = useElevationCellFocusEmitter();
    const columnName = props.column.name;
    const openingId = props.row.id;
    const openingName = props.row.name;
    const fingerprint = elevationFingerprint(props.row);
    const row = props.row;

    useEffect(() => {
        if(!emitter) return;
        const focus = cellFocusFor(row, columnName);
        if(!focus) return;

        emitter.emit(focus);
        return () => emitter.emit(null);
        // row: read only when the fingerprint (its previewed fields) changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emitter, columnName, openingId, openingName, fingerprint]);

    return null;
}

// the elevation tool url for an opening, focused on a workspace.
export function elevationToolUrl(props: {
    project: number;
    alternative?: number;
    openingId: number;
    workspace: Workspace;
    anchors?: boolean;
}) {
    const base = props.alternative !== undefined
        ? `/project/${props.project}/quote/${props.alternative}/elevation-tool?quoteOpening=${props.openingId}&alternative=${props.alternative}`
        : `/project/${props.project}/shop-drawing/elevation-tool?opening=${props.openingId}`;

    return base + `&workspace=${props.workspace}` + (props.anchors ? "&anchors=1" : "");
}
